/**
 * Application error code runtime utilities.
 *
 * Backend is the source of truth — see `docs/error-codes.md` for the full
 * catalog. The frontend mirror of that catalog lives in
 * `frontend/src/data/errorCodes.ts`; this file holds only the runtime helpers
 * (extract / format / classify) plus the FE-only constants used as
 * type-level identifiers in code paths.
 *
 * Range summary:
 *   10xxx auth         20xxx validation     30xxx network (FE-only)
 *   40xxx server       50xxx permission     60xxx resource
 *   70xxx business     80xxx database       9xxxx unknown (FE fallback)
 */

import { lookupBetterAuthSlug } from '../data/errorCodes';

export const FrontendErrorCode = {
	// Network — FE only
	NETWORK_TIMEOUT: '30001',
	NETWORK_OFFLINE: '30002',
	NETWORK_UNREACHABLE: '30003',
	NETWORK_ABORTED: '30004',

	// Unknown — FE fallback
	UNKNOWN_ERROR: '90001',
	PARSE_ERROR: '90002',
	EMPTY_RESPONSE: '90003',
	UNEXPECTED_SHAPE: '90004',
	/** Backend returned an unrecognized error slug we can't map. */
	UNMAPPED_SLUG: '90005',
	/** API returned null/undefined where a payload was expected. */
	NULL_PAYLOAD: '90006',
} as const;

/**
 * Map a Better Auth slug (e.g. `INVALID_EMAIL_OR_PASSWORD`) to our 5-digit
 * application code. Used by `useLoginForm` because Better Auth ships its own
 * error shape that bypasses the backend's error envelope.
 *
 * Mappings are declared inline in the catalog (`data/errorCodes.ts`) via
 * each row's `betterAuthSlugs` field — keeping slug ↔ code together prevents
 * drift between this lookup and the admin reference table.
 */
export function mapBetterAuthSlug(slug: string | undefined): string | null {
	return lookupBetterAuthSlug(slug);
}

export type FrontendErrorCode = (typeof FrontendErrorCode)[keyof typeof FrontendErrorCode];

/** Fallback code used only when the backend did not supply one. */
export const UNKNOWN_ERROR_CODE: string = FrontendErrorCode.UNKNOWN_ERROR;

/** Format a code for user display, e.g. `[E-10001]`. */
export function formatErrorCode(code: string): string {
	return `[E-${code}]`;
}

interface ErrorWithCode {
	code?: string | number;
	name?: string;
	message?: string;
	response?: { data?: { code?: string | number } };
	cause?: { code?: string | number };
}

/**
 * Extract an error code from an arbitrary error value.
 *
 * Order of precedence:
 *   1. `error.response.data.code`  — axios-style backend payload
 *   2. `error.cause.code`          — better-auth wraps fetch errors here
 *   3. `error.code`                — direct property (rethrows preserving code)
 *   4. Client-side classification  — network/abort/timeout when no response
 *   5. `UNKNOWN_ERROR_CODE`        — final fallback
 *
 * A code is considered usable when it is a non-empty trimmed string or a
 * finite number; numbers are coerced to their string form.
 */
export function extractErrorCode(error: unknown): string {
	if (!error || typeof error !== 'object') return UNKNOWN_ERROR_CODE;

	const e = error as ErrorWithCode;

	const fromBackend = e.response?.data?.code ?? e.cause?.code ?? e.code;
	const normalized = normalizeCode(fromBackend);
	if (normalized) return normalized;

	// Client-side classification when backend never responded.
	const msg = (e.message ?? '').toLowerCase();
	if (e.name === 'AbortError' || msg.includes('aborted')) {
		return FrontendErrorCode.NETWORK_ABORTED;
	}
	if (msg.includes('timeout')) return FrontendErrorCode.NETWORK_TIMEOUT;
	if (msg.includes('network error') || msg.includes('failed to fetch')) {
		return typeof navigator !== 'undefined' && navigator.onLine === false
			? FrontendErrorCode.NETWORK_OFFLINE
			: FrontendErrorCode.NETWORK_UNREACHABLE;
	}

	return UNKNOWN_ERROR_CODE;
}

function normalizeCode(raw: unknown): string | null {
	if (raw === undefined || raw === null) return null;
	if (typeof raw === 'number') return Number.isFinite(raw) ? String(raw) : null;
	const s = String(raw).trim();
	return s.length > 0 ? s : null;
}

/** Backend codes the mail / password-reset screens branch on. Full catalog: `data/errorCodes.ts`. */
export const MailErrorCode = {
	TOO_MANY_ATTEMPTS: '10009',
	MAIL_NOT_CONFIGURED: '40009',
	MAIL_SEND_FAILED: '40010',
} as const;

interface ErrorWithRetryAfter {
	response?: { status?: number; data?: { data?: { retryAfterSeconds?: unknown } } };
}

/** `data.retryAfterSeconds` of a 429 envelope, or null when absent/invalid. */
export function extractRetryAfterSeconds(error: unknown): number | null {
	if (!error || typeof error !== 'object') return null;
	const raw = (error as ErrorWithRetryAfter).response?.data?.data?.retryAfterSeconds;
	return typeof raw === 'number' && Number.isFinite(raw) && raw > 0 ? raw : null;
}

function isRateLimited(error: unknown, code: string): boolean {
	if (code === MailErrorCode.TOO_MANY_ATTEMPTS) return true;
	return (error as ErrorWithRetryAfter | null)?.response?.status === 429;
}

/** Vietnamese message for a failed mail-sending request (forgot password, SMTP test). */
export function describeMailError(error: unknown, fallback: string): string {
	const code = extractErrorCode(error);
	if (code === MailErrorCode.MAIL_NOT_CONFIGURED) {
		return 'Hệ thống chưa cấu hình gửi email, liên hệ quản trị viên.';
	}
	if (code === MailErrorCode.MAIL_SEND_FAILED) {
		return 'Gửi email thất bại, vui lòng thử lại sau.';
	}
	if (isRateLimited(error, code)) {
		const seconds = extractRetryAfterSeconds(error);
		return seconds
			? `Bạn đã thao tác quá nhiều lần. Vui lòng thử lại sau ${Math.ceil(seconds / 60)} phút.`
			: 'Bạn đã thao tác quá nhiều lần. Vui lòng thử lại sau.';
	}
	return `${fallback} Mã lỗi E-${code}`;
}

const OTP_ERROR_MESSAGES: Readonly<Record<string, string>> = {
	INVALID_OTP: 'Mã xác nhận không đúng.',
	OTP_EXPIRED: 'Mã xác nhận đã hết hạn. Vui lòng yêu cầu mã mới.',
	TOO_MANY_ATTEMPTS: 'Bạn đã nhập sai quá nhiều lần. Vui lòng yêu cầu mã mới.',
};

/** Vietnamese message for a Better Auth emailOTP error slug, or null when the slug is not OTP-specific. */
export function otpErrorMessage(slug: string | undefined | null): string | null {
	if (!slug) return null;
	return OTP_ERROR_MESSAGES[slug.toUpperCase()] ?? null;
}
